import { useState, type ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import {
  SelectedExhibitorEntriesList,
  type SelectedExhibitorEntriesListProps,
} from '@/components/entries/SelectedExhibitorEntriesList'
import type { TExhibitorWithEntries } from '@schemas'
import { useCheckInStepperContext } from '../CheckInStepper'

type EntriesStepProps = {
  exhibitor: TExhibitorWithEntries | null
  entryFormCard: ReactNode
  entriesListProps: SelectedExhibitorEntriesListProps | null
  finalizeDisabled?: boolean
  finalizeTooltip?: string
  finalizePending?: boolean
  onFinalizeEntrant?: (exhibitor: TExhibitorWithEntries) => Promise<TExhibitorWithEntries | null>
  backStepId: string
  finalizeStepId: string
  recentCheckIns?: ReactNode
}

export function EntriesStep({
  exhibitor,
  entryFormCard,
  entriesListProps,
  finalizeDisabled = false,
  finalizeTooltip,
  finalizePending = false,
  onFinalizeEntrant,
  backStepId,
  finalizeStepId,
  recentCheckIns,
}: EntriesStepProps) {
  const stepper = useCheckInStepperContext()
  const [finalizeInFlight, setFinalizeInFlight] = useState(false)

  const entriesCount = entriesListProps?.entries?.length ?? 0
  const resolvedEntriesListProps = entriesListProps && entriesCount > 0 ? entriesListProps : null
  const showEntriesList = Boolean(resolvedEntriesListProps)
  const finalizeBusy = finalizePending || finalizeInFlight
  const finalizeButtonDisabled =
    finalizeDisabled || finalizeBusy || !exhibitor || entriesCount === 0
  const finalizeTitle = !exhibitor
    ? 'Select an entrant before finalizing entries.'
    : entriesCount === 0
      ? 'Add at least one entry before finalizing entries.'
      : finalizeTooltip
  const finalizeButtonLabel = finalizeBusy ? 'Finalizing…' : 'Finalize Entries'

  const handleFinalize = async () => {
    if (!exhibitor || finalizeBusy) {
      return
    }

    if (!onFinalizeEntrant) {
      stepper.setSelectedEntrant(exhibitor)
      stepper.goToStep(finalizeStepId)
      return
    }

    try {
      setFinalizeInFlight(true)
      const result = await onFinalizeEntrant(exhibitor)
      if (result) {
        stepper.setSelectedEntrant(result)
        stepper.goToStep(finalizeStepId)
      }
    } catch {
      // Errors are surfaced by the caller.
    } finally {
      setFinalizeInFlight(false)
    }
  }

  return (
    <div className="space-y-6" data-testid="entries-step-content">
      <div>{entryFormCard}</div>

      {exhibitor ? (
        showEntriesList && resolvedEntriesListProps ? (
          <SelectedExhibitorEntriesList {...resolvedEntriesListProps} />
        ) : (
          <div className="rounded-md border border-dashed border-muted bg-background/60 p-3 text-xs text-muted-foreground">
            No entries yet. Use the form above to create the first entry.
          </div>
        )
      ) : (
        <div className="rounded-md border border-dashed border-muted bg-muted/20 p-4 text-sm text-muted-foreground">
          Select an entrant to review their entries and continue with intake.
        </div>
      )}

      {recentCheckIns ? <div>{recentCheckIns}</div> : null}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Button
          type="button"
          variant="outline"
          onClick={() => stepper.goToStep(backStepId)}
          disabled={stepper.activeStepId === backStepId}
        >
          Back to Entrant
        </Button>
        <Button
          type="button"
          disabled={finalizeButtonDisabled}
          title={finalizeTitle}
          onClick={handleFinalize}
        >
          {finalizeButtonLabel}
        </Button>
      </div>
    </div>
  )
}
